import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DashboardGranularity } from '../dateFilters';
import PanelLoading from '../../Loading/PanelLoading';
import * as S from './styles';

export interface ChartPoint {
  /** Chave do ponto: `YYYY-MM-DD` no modo diário, `YYYY-MM` no mensal. */
  key: string;
  /** Rótulo curto do eixo X. */
  label: string;
  /** Rótulo completo, usado no tooltip. */
  fullLabel: string;
  total: number;
}

const currency = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  });

const compactCurrency = (value: number) => {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(1)}mil`;
  return `R$ ${value.toFixed(0)}`;
};

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as ChartPoint | undefined;

  return (
    <S.TooltipCard>
      <span className="period">{point?.fullLabel}</span>
      <span className="value">{currency(payload[0]?.value ?? 0)}</span>
    </S.TooltipCard>
  );
}

export function ChartDashboard({
  points,
  granularity,
  periodTotal,
  rangeLabel,
  isFetching,
  isLoading,
}: {
  points: ChartPoint[];
  granularity: DashboardGranularity;
  /**
   * Faturamento do intervalo selecionado, vindo do `sales-summary` — a mesma
   * fonte dos KPIs do topo.
   *
   * Não somamos as barras para chegar a este número: no modo mensal cada
   * barra é um mês inteiro, então a soma cobriria um período maior que o
   * filtrado. Além disso, somar aqui faria o título divergir do card
   * "Faturamento do Período" sempre que as duas endpoints discordassem.
   */
  periodTotal: number;
  /** Intervalo por extenso, para deixar claro a que o total se refere. */
  rangeLabel?: string;
  isFetching?: boolean;
  /** Primeira carga: ainda não sabemos se o período tem faturamento. */
  isLoading?: boolean;
}) {
  const { average, best } = useMemo(() => {
    if (!points.length) return { average: 0, best: null as ChartPoint | null };

    const sum = points.reduce((acc, point) => acc + point.total, 0);
    const bestPoint = points.reduce(
      (max, point) => (point.total > max.total ? point : max),
      points[0]
    );

    return { average: sum / points.length, best: bestPoint };
  }, [points]);

  /**
   * No modo diário com 30 pontos, rotular todos os dias polui o eixo — pulamos
   * um a cada dois. Com 7 dias e no modo mensal cabem todos os rótulos.
   */
  const tickInterval = granularity === 'day' && points.length > 14 ? 1 : 0;

  const hasValues = points.some((point) => point.total > 0);

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Eyebrow>Visão Geral do Faturamento</S.Eyebrow>
          <S.TotalValue>{isLoading ? '—' : currency(periodTotal)}</S.TotalValue>
          {rangeLabel && <S.RangeCaption>{rangeLabel}</S.RangeCaption>}
        </div>

        {!isLoading && points.length > 0 && (
          <S.Stats>
            <div className="stat">
              <span className="label">
                {granularity === 'day' ? 'Média por dia' : 'Média por mês'}
              </span>
              <span className="value">{compactCurrency(average)}</span>
            </div>
            {best && (
              <div className="stat">
                <span className="label">
                  {granularity === 'day' ? 'Melhor dia' : 'Melhor mês'}
                </span>
                <span className="value">{best.label}</span>
              </div>
            )}
          </S.Stats>
        )}
      </S.Header>

      {isFetching && !isLoading && <S.UpdatingBadge>Atualizando…</S.UpdatingBadge>}

      {isLoading ? (
        // Reserva a mesma altura do gráfico para a tela não "pular" quando os
        // dados chegarem.
        <PanelLoading height={280} />
      ) : points.length > 0 && hasValues ? (
        <S.ChartArea>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={points} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="faturamentoFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#818CF8" stopOpacity={0.7} />
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} strokeDasharray="4 8" stroke="#EEF0F5" />

              <XAxis
                dataKey="label"
                stroke="#9CA3AF"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                dy={8}
                interval={tickInterval}
              />

              <YAxis
                stroke="#9CA3AF"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                width={64}
                tickFormatter={compactCurrency}
              />

              <Tooltip
                cursor={{ fill: 'rgba(99, 102, 241, 0.08)' }}
                content={<CustomTooltip />}
              />

              <Bar
                dataKey="total"
                fill="url(#faturamentoFill)"
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          </ResponsiveContainer>
        </S.ChartArea>
      ) : (
        <S.NotData>Ainda não há faturamento no período selecionado!</S.NotData>
      )}
    </S.Container>
  );
}
